import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import customFetch from '../../../../utils/customFetch';
import { CheckIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';

const allOrdersQuery = {
  queryKey: ['allOrders'],
  queryFn: async () => {
    const data = await customFetch.get('/orders');
    return data;
  },
};

export const loader = (queryClient) => async () => {
  const data = await queryClient.ensureQueryData(allOrdersQuery);
  return data;
};

const OrderList = () => {
  const { data } = useQuery(allOrdersQuery);
  const orders = data?.data?.allOrders || [];

  // State für den aktiven Filter
  const [statusFilter, setStatusFilter] = useState('Alle');

  // Sammle alle möglichen Status-Werte
  const allStatuses = ['Alle', ...new Set(orders.map((order) => order.status))];

  // Filtere Bestellungen basierend auf dem ausgewählten Status
  const filteredOrders =
    statusFilter === 'Alle'
      ? orders
      : orders.filter((order) => order.status === statusFilter);

  return (
    <>
      <div className="bg-white px-6 py-6 sm:py-6 lg:px-8">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-base font-semibold leading-6 text-gray-900">
              Bestellverwaltung
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              Hier sind alle Bestellungen aufgelistet.
            </p>
          </div>
        </div>

        <div className="mt-8 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3"
                    >
                      Bestellcode
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      <Menu
                        as="div"
                        className="relative inline-block text-left"
                      >
                        <MenuButton className="group inline-flex items-center focus:outline-none">
                          Status
                          <span className="ml-2 flex-none rounded-sm bg-gray-100 text-gray-900 group-hover:bg-gray-200">
                            <ChevronDownIcon
                              className="size-5"
                              aria-hidden="true"
                            />
                          </span>
                        </MenuButton>
                        <MenuItems
                          transition
                          className="absolute left-0 z-10 mt-1 w-40 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 transition focus:outline-hidden data-closed:scale-95 data-closed:transform data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in"
                        >
                          <div className="py-1">
                            {allStatuses.map((status) => (
                              <MenuItem key={status}>
                                <button
                                  onClick={() => setStatusFilter(status)}
                                  className={`block w-full px-4 py-2 text-left text-sm ${
                                    statusFilter === status
                                      ? 'bg-gray-100 text-gray-900'
                                      : 'text-gray-700'
                                  } data-focus:bg-gray-100 data-focus:text-gray-900 data-focus:outline-hidden`}
                                >
                                  {status}
                                </button>
                              </MenuItem>
                            ))}
                          </div>
                        </MenuItems>
                      </Menu>
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Total
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Anzahl Karten
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Datum
                    </th>
                    <th
                      scope="col"
                      className="relative py-3.5 pl-3 pr-4 sm:pr-3"
                    >
                      <span className="sr-only">Bearbeiten</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {filteredOrders.map((order) => (
                    <tr key={order.orderNumber} className="even:bg-gray-50">
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3">
                        {order.orderNumber}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {order.status === 'Bezahlt' ? (
                          <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20">
                            {order.status}
                          </span>
                        ) : order.status === 'Fehlgeschlagen' ||
                          order.status === 'Abgebrochen' ? (
                          <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/20">
                            {order.status}
                          </span>
                        ) : order.status === 'Geliefert' ? (
                          <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                            {order.status} <CheckIcon className="h-4 w-4" />
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-700 ring-1 ring-inset ring-yellow-600/20">
                            {order.status}
                          </span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {order.total / 100} CHF
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {order.orderItems.length}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {order.createdAt.split('T')[0]}
                      </td>
                      <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-3">
                        <a
                          href={`/dashboard/bestellbearbeitung/${order._id}`}
                          className="text-svisit-primary-600 hover:text-svisit-primary-900"
                        >
                          Bearbeiten
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderList;
